import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { Metamodel } from 'app/model/base/model';
import { WebActivityLinkedService } from 'app/model/linkedservice/web-activity.linkedservice';
import { GlobalVariables } from 'app/services/globals';
import { ConnectionAuthKind, CredentialTypeOptions } from 'shell-models-lib';

const baseUrl: ControlMetadata = {
    type: 'textbox',
    bind: 'url',
    required: true,
    validators: [
        { type: 'required', errorMessage: '' },
        { type: 'url'}
    ],
    label: stringResources.ModelName_BaseUrl,
    isLabelVertical: true,
    placeholder: stringResources.WebActivityBaseURLText,
    description: stringResources.WebActivityBaseURLTooltip
};

const dynamicBaseUrl: ControlMetadata = {
    type: 'parameterizable',
    child: baseUrl
};

const username: ControlMetadata = {
    type: 'textbox',
    bind: 'userName',
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    label: stringResources.UsernameLabel,
    isLabelVertical: true
};

const dynamicUsername: ControlMetadata = {
    type: 'parameterizable',
    child: username
};

const passwordSetting: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    enableAKVParameterization: true,
    akvOnly: true,
    bind: 'password',
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    label: stringResources.ModelName_Password,
    child: {
        type: 'password',
        label: stringResources.ModelName_Password,
        required: true,
        isLabelVertical: true
    }
};

const pfxSetting: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    enableAKVParameterization: true,
    akvOnly: true,
    bind: 'pfx',
    required: true,
    label: stringResources.WebActivity_Pfx,
    isCert: true,
    child: {
        type: 'password',
        label: stringResources.WebActivity_Pfx,
        required: true,
        isLabelVertical: true
    }
};

const authenticationReferenceMethodType: ControlMetadata = {
    type: 'radio-switch',
    nested: 1,
    label: stringResources.ModelName_ServicePrincipalCredentialType,
    options: [
        {
            value: stringResources.Inline,
            label: stringResources.Inline
        },
        {
            value: stringResources.ModelName_Credential,
            label: stringResources.ModelName_Credential
        }
    ],
    isLabelVertical: true,
    defaultValue: '{isSpnAuthRefMethodCredential ? "Credential" : "Inline"}',
    cases: [
        {
            caseValue: stringResources.Inline,
            children: [
                {
                    type: 'parameterizable',
                    child: {
                        type: 'textbox',
                        bind: 'tenant',
                        required: true,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        label: stringResources.ModelName_Tenant,
                        isLabelVertical: true
                    }
                },
                {
                    type: 'parameterizable',
                    child: {
                        type: 'textbox',
                        bind: 'servicePrincipalId',
                        required: true,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        label: stringResources.ModelName_ServicePrincipalID,
                        isLabelVertical: true
                    }
                },
                {
                    type: 'radio-switch',
                    nested: 1,
                    isLabelVertical: true,
                    label: stringResources.ModelName_ServicePrincipalKeyType,
                    options: [
                        {
                            label: stringResources.ModelName_ServicePrincipalKey,
                            value: stringResources.ModelName_ServicePrincipalKey
                        },
                        {
                            label: stringResources.ModelName_ServicePrincipalCert,
                            value: stringResources.ModelName_ServicePrincipalCert
                        }
                    ],
                    defaultValue: '{isSpnAuthKey ? "Service principal key" : "Service principal certificate"}',
                    cases: [
                        {
                            caseValue: stringResources.ModelName_ServicePrincipalKey,
                            children: [
                                {
                                    type: 'dynamic-akv-switcher',
                                    enableAKVParameterization: true,
                                    akvOnly: true,
                                    bind: 'servicePrincipalKey',
                                    required: true,
                                    label: stringResources.ModelName_ServicePrincipalKey,
                                    child: {
                                        type: 'password',
                                        label: stringResources.ModelName_ServicePrincipalKey,
                                        required: true,
                                        isLabelVertical: true
                                    }
                                }
                            ]
                        },
                        {
                            caseValue: stringResources.ModelName_ServicePrincipalCert,
                            children: [
                                {
                                    type: 'dynamic-akv-switcher',
                                    enableAKVParameterization: true,
                                    akvOnly: true,
                                    bind: 'pfx',
                                    required: true,
                                    label: stringResources.ModelName_ServicePrincipalCert,
                                    isCert: true,
                                    child: {
                                        type: 'password',
                                        label: stringResources.ModelName_ServicePrincipalCert,
                                        required: true,
                                        isLabelVertical: true
                                    }
                                }
                            ]
                        }
                    ]
                }
            ]
        },
        {
            caseValue: stringResources.ModelName_Credential,
            children: [
                {
                    type: 'credential-select',
                    bind: 'credential',
                    filterCredentialType: CredentialTypeOptions.ServicePrincipal,
                    required: true,
                    validators: [
                        { type: 'required', errorMessage: '' }
                    ]
                }
            ]
        }
    ]
};

const resource: ControlMetadata = {
    type: 'textbox',
    bind: 'aadResourceId',
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    label: stringResources.Resource,
    isLabelVertical: true
};

const dynamicResource: ControlMetadata = {
    type: 'parameterizable',
    child: resource
};

const authentication: ControlMetadata = {
    type: 'select-switch',
    isLabelVertical: true,
    bind: 'authentication',
    label: stringResources.Authentication,
    options: [
        {
            label: stringResources.Anonymous_DisplayText,
            value: 'Anonymous'
        },
        {
            label: stringResources.Basic_DisplayText,
            value: 'Basic',
            description: stringResources.WebActivity_BasicAuthDescription
        },
        {
            label: stringResources.AuthenticationType_ManagedServiceIdentity,
            value: 'ManagedServiceIdentity',
            description: stringResources.WebActivity_MSIAuthDescription
        },
        {
            label: stringResources.ClientCertificate_DisplayText,
            value: 'ClientCertificate',
            description: stringResources.WebActivity_ClientCertAuthDescription
        },
        {
            label: stringResources.AuthenticationType_ServicePrincipal,
            value: 'AadServicePrincipal',
            tooltip: stringResources.WebActivity_ServicePrincipalAuthDescription
        },
        {
            label: stringResources.AuthenticationType_UserAssignedManagedIdentity,
            value: 'UserAssignedManagedIdentity',
            tooltip: stringResources.WebActivity_UserAssignedManagedIdentityAuthDescription
        },
        {
            label: __IS_FABRIC_DPE__? stringResources.ModelName_UserAuthentication_NoPreview : stringResources.ModelName_UserAuthentication,
            value: 'UserAuth',
            tooltip: stringResources.ModelName_UserAuthentication,
            hidden: !GlobalVariables.enableUserAuthCred
        }
    ],
    defaultValue: stringResources.Anonymous_DisplayText,
    description: stringResources.WebActivityAuthenticationTooltip,
    cases: [
        {
            caseValue: 'Basic',
            children: [
                dynamicUsername,
                passwordSetting
            ]
        },
        {
            caseValue: 'ClientCertificate',
            children: [
                pfxSetting,
                passwordSetting
            ]
        },
        {
            caseValue: 'ManagedServiceIdentity',
            children: [
                dynamicResource,
                {
                    type: 'credential-select',
                    bind: 'credential',
                    filterCredentialType: CredentialTypeOptions.ManagedIdentity,
                    hide: !GlobalVariables.isSamiSupportEnabled,
                    required: true,
                    validators: [
                        { type: 'required', errorMessage: '' }
                    ]
                }
            ]
        },
        {
            caseValue: 'AadServicePrincipal',
            children: [
                authenticationReferenceMethodType,
                dynamicResource
            ]
        },
        {
            caseValue: 'UserAssignedManagedIdentity',
            children: [
                dynamicResource,
                {
                    type: 'credential-select',
                    bind: 'credential',
                    filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                    required: true,
                    validators: [
                        { type: 'required', errorMessage: '' }
                    ]
                }
            ]
        },
        {
            caseValue: 'UserAuth',
            children: [
                dynamicResource,
                {
                    type: 'credential-select',
                    bind: 'credential',
                    filterCredentialType: CredentialTypeOptions.UserAuth,
                    required: true,
                    validators: [
                        { type: 'required', errorMessage: '' }
                    ]
                }
            ]
        }
    ]
};

const serverCerfiticateValidation: ControlMetadata = {
    type: 'radio',
    bind: 'enableServerCertificateValidation',
    label: stringResources.ModelName_EnableServerCertificateValidation,
    description: stringResources.HttpEnableServerCertificateValidationDescription,
    isLabelVertical: true,
    options: [
        {
            label: stringResources.Enable,
            value: true
        },
        {
            label: stringResources.Disable,
            value: false
        }
    ],
    defaultValue: true
};

export const webActivityMetadata: ILinkedServiceTypeMetadata = {
    type: Metamodel.modelFor(WebActivityLinkedService).name,
    displayName: __IS_FABRIC_DPE__ ? stringResources.WebV2_DisplayTest : stringResources.WebConnection_DisplayText,
    supportedInTrident: true,
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: 'WebForPipeline' as ResourceKind
    },
    supportOPDG: true,
    icon: {
        className: 'svg-i_web_16x16'
    },
    category: ['GenericProtocol', 'ServicesApps'],
    supportedAuthKindsInTrident: [ConnectionAuthKind.Anonymous, ConnectionAuthKind.Basic, ConnectionAuthKind.OAuth2, ConnectionAuthKind.ServicePrincipal] as ConnectionAuthKind[],
    isCompute: true,
    isFirstClass: true,
    copySinkTypes: [],
    copySourceTypes: [],
    disableQueryAPI: true,
    supportFirstClassParameterExperience: true,
    controls: [
        dynamicBaseUrl,
        serverCerfiticateValidation,
        authentication
    ]
};
